import { useState } from 'react';

import { Container } from '@/atoms';
import { InfoSeller } from '@/organisms';
import { FlexibleContent, HeroSlider } from '@/templates';
import { getBaseLayout } from '@/layouts/BaseLayout';

import ModelViewer from '@/components/3d/ModelViewer';
import { getInitialProps } from '@/lib/graphql/api';
import {
  FlexibleContentBlocks,
  pageAgeGate,
  pageHeaderFooter,
  pageTheme,
  seo,
  sliderHero,
} from '@/lib/graphql/partials';
import { PageQuery } from '@/lib/graphql/querys';
import { pagePropsServerTransformer } from '@/lib/graphql/transformers';
import { routes } from '@/lib/routes';
import {
  LATO,
  RED_HAT_DISPLAY,
  SAA_SERIES_B_DOT,
  SAA_SERIES_C_DOT,
} from '@/lib/utils/fonts';

import type { NextPageWithLayout } from 'pages/_app';

const Huebox3D = () => {
  const [zoom, setZoom] = useState(false);

  return (
    <ModelViewer
      alt="Huebox"
      src="/huebox.glb"
      shadow-intensity={1}
      camera-controls
      touch-action="pan-y"
      className="-mt-14 h-[600px] w-full"
      auto-rotate
      poster="/img-huebox-home-poster-es.webp"
      {...(!zoom && { 'disable-zoom': true })}
      disable-tap
      scale="1.6 1.6 1.6"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
              :root {
                --font-lato: ${LATO.style.fontFamily};
                --font-red-hat-display: ${RED_HAT_DISPLAY.style.fontFamily};
                --font-saa-series-b-dot: ${SAA_SERIES_B_DOT.style.fontFamily};
                --font-saa-series-c-dot: ${SAA_SERIES_C_DOT.style.fontFamily};
              }
              button[slot*=hotspot]:not([data-visible]){
                background: transparent;
                border: 4px solid #fff;
                box-shadow: none;
                height: 32px;
                pointer-events: none;
                width: 32px;
              }
            
              button[slot*=hotspot]:focus,
              button[slot*=hotspot]:hover{
                border: 4px solid #ec342e;
                height: 32px;
                outline: none;
                width: 32px;
              }
            
              button[slot*=hotspot]:focus > .HotspotAnnotation,
              button[slot*=hotspot]:hover > .HotspotAnnotation{
                opacity: 1;
                pointer-events: all;
              }
            
              button[slot*=hotspot]:not([data-visible]):focus > *,
              button[slot*=hotspot]:not([data-visible]):hover > *{
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.3s;
              }
              .fontLato{
                font-family : var(--font-lato);
              }
              .fontSaaB{
                font-family : var(--font-saa-series-b-dot);
              }
              button{
                text-transform: inherit;
              }
            `,
        }}
      />
      <button
        className="relative box-border h-6 w-6 cursor-pointer rounded-[32px] border-0 bg-white p-2 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.3s]"
        slot="hotspot-1"
        data-position="-0.05 2.2 1.95"
        data-normal="0 1 1"
        data-visibility-attribute="visible"
        data-visible=""
      >
        <div className="absolute left-2/4 top-[calc(50%_-_1px)] -translate-x-2/4 -translate-y-2/4 text-sm font-light">
          1
        </div>
        <div className="HotspotAnnotation pointer-events-none absolute top-[35px] block w-max max-w-[260px] -translate-x-2/4 translate-y-0 break-words rounded bg-white px-[1em] py-[0.5em] text-left text-[rgba(0,0,0,0.8)] opacity-0 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.4s] ease-[ease]">
          <div className="fontSaaB text-lg font-bold leading-[1.2]">
            Rápido Enfriado
          </div>
          <div className="fontLato text-sm font-light leading-[1.2]">
            Disfruta de tu Estrella Galicia siempre fresca gracias al sistema de
            enfriado de Huebox, capaz de refrigerar los 3 litros de tu Keg en
            tan solo 3 horas. Porque tomarse una cerveza bien fresquita en
            pijama está infravalorado.{' '}
          </div>
        </div>
      </button>
      <button
        className="relative box-border h-6 w-6 cursor-pointer rounded-[32px] border-0 bg-white p-2 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.3s]"
        slot="hotspot-2"
        data-position="-0.05 3.2 2.55"
        data-normal="0 1 1"
        data-visibility-attribute="visible"
        data-visible=""
      >
        <div className="absolute left-2/4 top-[calc(50%_-_1px)] -translate-x-2/4 -translate-y-2/4 text-sm font-light">
          2
        </div>
        <div className="HotspotAnnotation pointer-events-none absolute top-[35px] block w-max max-w-[260px] -translate-x-2/4 translate-y-0 break-words rounded bg-white px-[1em] py-[0.5em] text-left text-[rgba(0,0,0,0.8)] opacity-0 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.4s] ease-[ease]">
          <div className="fontSaaB text-lg font-bold leading-[1.2]">
            No requiere limpieza de circuito
          </div>
          <div className="fontLato text-sm font-light leading-[1.2]">
            Si piensas que esto va a suponer un mantenimiento exhaustivo, te
            equivocas, porque el mantenimiento y la limpieza del interior es
            cosa sencilla, de andar por casa, cómodo, como a ti te gusta, con
            los menores paños posibles.{' '}
          </div>
        </div>
      </button>
      <button
        className="relative box-border h-6 w-6 cursor-pointer rounded-[32px] border-0 bg-white p-2 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.3s]"
        slot="hotspot-3"
        data-position="1.55 1.8 0"
        data-normal="1 1 0"
        data-visibility-attribute="visible"
        data-visible=""
      >
        <div className="absolute left-2/4 top-[calc(50%_-_1px)] -translate-x-2/4 -translate-y-2/4 text-sm font-light">
          3
        </div>
        <div className="HotspotAnnotation pointer-events-none absolute top-[35px] block w-max max-w-[260px] -translate-x-2/4 translate-y-0 break-words rounded bg-white px-[1em] py-[0.5em] text-left text-[rgba(0,0,0,0.8)] opacity-0 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.4s] ease-[ease]">
          <div className="fontSaaB text-lg font-bold leading-[1.2]">
            Sin uso de CO2
          </div>
          <div className="fontLato text-sm font-light leading-[1.2]">
            El barril Keg está diseñado para albergar en su interior el CO2 y
            hacer que la experiencia de tomarte tu Estrella Galicia en casa sea
            perfecta. ¿Acaso te lo podemos poner más a huevo?{' '}
          </div>
        </div>
      </button>
      <button
        className="relative box-border h-6 w-6 cursor-pointer rounded-[32px] border-0 bg-white p-2 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.3s]"
        slot="hotspot-4"
        data-position="0 4 -1"
        data-normal="0 0 -1"
        data-visibility-attribute="visible"
      >
        <div className="absolute left-2/4 top-[calc(50%_-_1px)] -translate-x-2/4 -translate-y-2/4 text-sm font-light">
          4
        </div>
        <div className="HotspotAnnotation pointer-events-none absolute top-[35px] block w-max max-w-[260px] -translate-x-2/4 translate-y-0 break-words rounded bg-white px-[1em] py-[0.5em] text-left text-[rgba(0,0,0,0.8)] opacity-0 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.4s] ease-[ease]">
          <div className="fontSaaB text-lg font-bold leading-[1.2]">
            Barril reciclable y desechable
          </div>
          <div className="fontLato text-sm font-light leading-[1.2]">
            Lo bueno de que sea una experiencia redonda es que lo sea tanto para
            ti como para el planeta. Nuestros barriles Keg de 3 litros albergan
            más líquido en un solo recipiente y están diseñados para ser
            reciclados y desechados en el contenedor amarillo. Porque todos
            queremos un planeta mejor, con más cerveza y menos desperdicios.
          </div>
        </div>
      </button>
      <button
        className="relative box-border h-6 w-6 cursor-pointer rounded-[32px] border-0 bg-white p-2 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.3s]"
        slot="hotspot-5"
        data-position="0.9 0.4 -1"
        data-normal="0.7 0.3 -0.6"
        data-visibility-attribute="visible"
      >
        <div className="absolute left-2/4 top-[calc(50%_-_1px)] -translate-x-2/4 -translate-y-2/4 text-sm font-light">
          5
        </div>
        <div className="HotspotAnnotation pointer-events-none absolute top-[35px] block w-max max-w-[260px] -translate-x-2/4 translate-y-0 break-words rounded bg-white px-[1em] py-[0.5em] text-left text-[rgba(0,0,0,0.8)] opacity-0 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.4s] ease-[ease]">
          <div className="fontSaaB text-lg font-bold leading-[1.2]">
            Consumo energético eficiente
          </div>
          <div className="fontLato text-sm font-light leading-[1.2]">
            La cerveza en tu casa ahora es de bajo consumo, gracias al sistema
            de refrigeración de Huebox, que usa eficientemente la energía para
            mantener tu cerveza fresca en su circuito de enfriamiento
          </div>
        </div>
      </button>
      <button
        className="relative box-border h-6 w-6 cursor-pointer rounded-[32px] border-0 bg-white p-2 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.3s]"
        slot="hotspot-6"
        data-position="-0.05 1.2 1.60"
        data-normal="0 1 1"
        data-visibility-attribute="visible"
        data-visible=""
      >
        <div className="absolute left-2/4 top-[calc(50%_-_1px)] -translate-x-2/4 -translate-y-2/4 text-sm font-light">
          6
        </div>
        <div className="HotspotAnnotation pointer-events-none absolute top-[35px] block w-max max-w-[260px] -translate-x-2/4 translate-y-0 break-words rounded bg-white px-[1em] py-[0.5em] text-left text-[rgba(0,0,0,0.8)] opacity-0 shadow-[0_2px_4px_rgba(0,0,0,0.25)] transition-opacity duration-[0.4s] ease-[ease]">
          <div className="fontSaaB text-lg font-bold leading-[1.2]">
            Indicador máquina preparada
          </div>
          <div className="fontLato text-sm font-light leading-[1.2]">
            La máquina estará lista para tirar cerveza cuando el testigo se
            ponga azul.
          </div>
        </div>
      </button>
      <label
        className="absolute bottom-4 right-4 flex items-center gap-2 text-white"
        htmlFor="zoom"
      >
        <input
          type="checkbox"
          checked={zoom}
          onChange={(e) => setZoom(e.target.checked)}
          name="zoom"
          id="zoom"
          className="text-secondary-500 accent-secondary-500"
        />
        Zoom
      </label>
    </ModelViewer>
  );
};

const Brand: NextPageWithLayout = ({
  pageProps: { hero, flexibleContent, title, content, pageName },
}: any) => (
  <>
    <div data-pagename={pageName} />
    <HeroSlider hero={hero} />
    <FlexibleContent flexible={flexibleContent?.flexible} type="Marca" />
    {content && (
      <InfoSeller
        title={`Sobre ${title}`}
        description={content}
        backgroundColor="#F8F7F4"
      />
    )}
    <Container
      wrapper
      padding="px-4 md:pb-8 pb-4"
      backgroundColor="bg-[#262626]"
      className="h-[550px] overflow-hidden"
    >
      <Huebox3D />
    </Container>
  </>
);

export async function getStaticProps() {
  const content = `
    marca(id: $slug, idType: SLUG) {
      ${seo}
      ${pageTheme}
      ${pageAgeGate}
      ${pageHeaderFooter}
      title
      content
      hero:${sliderHero('Marca')}
      flexibleContent: ${FlexibleContentBlocks('Marca')}
    }
  `;
  const Query = PageQuery(content, '', 'Marca');

  return await pagePropsServerTransformer({
    staticProps: await getInitialProps(Query, { slug: 'huebox' }),
    type: 'Marca',
    path: routes.brand('huebox'),
  });
}

Brand.getLayout = getBaseLayout;

export default Brand;
